import type { Messages } from "../messages";
import { plural } from "../types";

export const de: Messages = {
  common: {
    copy: "Kopieren",
    copied: "Kopiert",
    optional: "Optional",
    show: "Anzeigen",
    hide: "Ausblenden",
    loading: "Wird geladen…",
    on: "An",
    off: "Aus",
  },

  language: {
    label: "Sprache",
  },

  header: {
    encrypted: "Ende-zu-Ende-verschlüsselt",
  },

  bytes: {
    b: "{size} B",
    kb: "{size} KB",
    mb: "{size} MB",
    gb: "{size} GB",
  },

  duration: {
    // Nominativ — steht für sich (Chip, Beleg).
    standalone: {
      hours: plural({ one: "{count} Stunde", other: "{count} Stunden" }),
      days: plural({ one: "{count} Tag", other: "{count} Tage" }),
      weeks: plural({ one: "{count} Woche", other: "{count} Wochen" }),
    },
    // Dativ — nach "läuft in … ab".
    inSentence: {
      hours: plural({ one: "{count} Stunde", other: "{count} Stunden" }),
      days: plural({ one: "{count} Tag", other: "{count} Tagen" }),
      weeks: plural({ one: "{count} Woche", other: "{count} Wochen" }),
    },
  },

  create: {
    mode: { text: "Text", file: "Datei" },
    textPlaceholder: "Fügen Sie ein Passwort, einen API-Schlüssel, einen privaten Schlüssel ein…",

    generator: {
      open: "Passwort generieren",
      close: "Generator schließen",
      length: "Länge",
      lowercase: "Kleinbuchstaben (a–z)",
      uppercase: "Großbuchstaben (A–Z)",
      digits: "Ziffern (0–9)",
      symbols: "Sonderzeichen (!@#…)",
      excludeAmbiguous: "Verwechselbare Zeichen vermeiden (0/O, 1/l/I)",
      needCharset: "Wählen Sie mindestens eine Zeichenart.",
      generate: "Generieren und einfügen",
      note: "Wird in Ihrem Browser erzeugt und an der Cursorposition im Feld unten eingefügt – oder am Ende, wenn Sie keinen Cursor gesetzt haben.",
    },

    dropzone: {
      title: "Datei hier ablegen",
      hint: "oder {browse} — bis zu {size}",
      browse: "durchsuchen",
      note: "verschlüsselt, bevor es dieses Gerät verlässt",
      remove: "Entfernen",
    },

    encStatus:
      "In Ihrem Browser verschlüsselt. Der Schlüssel steckt im Link, niemals auf unseren Servern.",

    expiry: {
      label: "Läuft ab nach",
      custom: "Benutzerdefiniert",
      unitHours: "Stunden",
      unitDays: "Tage",
      unitWeeks: "Wochen",
    },

    oneTime: {
      title: "Nach einmaliger Ansicht vernichten",
      description:
        "Das Geheimnis wird in dem Moment gelöscht, in dem es geöffnet wird. Ein zweiter Besuch sieht nichts mehr.",
    },

    allowedEmail: {
      label: "Auf eine E-Mail-Adresse beschränken",
      hint: "Alle, die den Link öffnen, bestätigen eine E-Mail-Adresse mit einem Einmalcode. Wird hier eine Adresse genannt, kommt nur diese Person durch.",
      placeholder: "nur-diese-person@beispiel.de",
    },

    notifyEmail: {
      title: "Benachrichtigen, wenn angesehen",
      description:
        "Bei jedem Öffnen des Geheimnisses geht eine Lesebestätigung heraus, die die öffnende Adresse nennt.",
      label: "Bestätigung senden an",
      placeholder: "sie@beispiel.de",
    },

    developer: {
      title: "Entwickleroptionen",
      webhookUrlLabel: "Webhook-URL",
      webhookUrlHint: "Wir senden bei Erstellung und bei jeder Ansicht ein JSON-Ereignis per POST.",
      webhookSecretLabel: "Webhook-Signaturschlüssel",
      webhookSecretPlaceholder: "mindestens 16 Zeichen",
      webhookSecretHint:
        "Signiert die Nutzdaten (HMAC-SHA256, gesendet im Header {header}). Erforderlich, sobald eine Webhook-URL gesetzt ist.",
    },

    policy: {
      open: "Dieser Link läuft in {expiry} ab. Alle, die den Link haben, können ihn nach Bestätigung einer E-Mail-Adresse öffnen.",
      openOneTime:
        "Dieser Link läuft in {expiry} ab oder in dem Moment, in dem er geöffnet wird — je nachdem, was zuerst eintritt. Alle, die den Link haben, können ihn nach Bestätigung einer E-Mail-Adresse öffnen.",
      restricted: "Dieser Link läuft in {expiry} ab. Nur {email} kann ihn öffnen.",
      restrictedOneTime:
        "Dieser Link läuft in {expiry} ab oder in dem Moment, in dem er geöffnet wird — je nachdem, was zuerst eintritt. Nur {email} kann ihn öffnen.",
    },

    submit: "Geheimen Link erstellen",
    submitting: "Wird verschlüsselt…",

    error: {
      needText: "Geben Sie einen Text zum Teilen ein.",
      needFile: "Wählen Sie eine Datei zum Teilen aus.",
      needNotifyEmail:
        "Geben Sie eine Adresse für die Lesebestätigung ein, oder schalten Sie die Benachrichtigung aus.",
      webhookSecretTooShort: "Der Webhook-Signaturschlüssel muss mindestens 16 Zeichen lang sein.",
      generic: "Beim Erstellen des Geheimnisses ist etwas schiefgelaufen.",
    },

    result: {
      title: "Ihr Link ist fertig",
      subtitle: "Verschicken Sie ihn, wie Sie möchten. Ohne den Teil nach dem Rautezeichen ist der Link nutzlos.",
      linkCaption: "Der tonfarbene Teil ist der Entschlüsselungsschlüssel — wir bekommen ihn nie zu sehen",
      expires: "Läuft ab",
      destroyAfterOneView: "Nach einmaliger Ansicht vernichten",
      restrictedTo: "Beschränkt auf",
      anyVerifiedEmail: "Jede bestätigte Adresse",
      createAnother: "Weiteren erstellen",
    },
  },

  retrieve: {
    invalidLink: {
      title: "Ungültiger Link",
      body: "Diesem Link fehlt sein Entschlüsselungsschlüssel. Achten Sie darauf, die vollständige URL kopiert zu haben — alles nach dem {hash} eingeschlossen.",
      note: "Wenn Sie diese Seite neu geladen haben, öffnen Sie den ursprünglichen Link noch einmal: Der Schlüssel wird beim Laden der Seite sofort aus der Adressleiste entfernt, damit er weder im Verlauf noch in einem Screenshot noch in irgendetwas landet, das diese Seite meldet.",
    },

    notFound: {
      title: "Geheimnis nicht gefunden",
      body: "Dieses Geheimnis ist abgelaufen, wurde bereits angesehen (sofern es einmalig war) oder hat nie existiert.",
    },

    alreadyRetrieved: {
      title: "Bereits geöffnet",
      body: "Dies war ein einmaliges Geheimnis, und jemand hat es bereits geöffnet. Es lässt sich nicht erneut lesen — weder von Ihnen noch von jemand anderem.",
      note: "Falls Sie dieser Jemand waren und der Download nicht fertig wurde, versuchen Sie den Link in den nächsten Minuten noch einmal. Danach ist das Geheimnis endgültig fort, und wer es geschickt hat, muss es erneut teilen.",
    },

    decrypting: "Wird entschlüsselt…",

    fatalError: {
      title: "Etwas ist schiefgelaufen",
      cannotReachServer: "Der Server war nicht erreichbar.",
      cannotRequestCode: "Es konnte kein Bestätigungscode angefordert werden.",
      cannotVerifyCode: "Der Code konnte nicht überprüft werden.",
      decryptionFailed: "Entschlüsselung fehlgeschlagen — der Link ist womöglich beschädigt oder unvollständig.",
    },

    email: {
      eyebrow: "Geheimnis wartet",
      title: "Jemand hat Ihnen etwas geschickt",
      body: "Bestätigen Sie Ihre E-Mail-Adresse, um es zu öffnen. Der Code ist zehn Minuten lang gültig.",
      fileLine: "Datei: {filename} ({size}). ",
      label: "Ihre E-Mail-Adresse",
      placeholder: "sie@beispiel.de",
      submit: "Code senden",
      submitting: "Wird gesendet…",
      restricted: "Dieses Geheimnis ist auf eine bestimmte E-Mail-Adresse beschränkt, und das ist nicht diese.",
      locked: "Zu viele falsche Versuche. Fordern Sie einen neuen Code an.",
    },

    otp: {
      title: "Geben Sie Ihren Code ein",
      body: "Wir haben einen Einmalcode an {email} gesendet.",
      label: "Einmalcode",
      submit: "Geheimnis entsperren",
      submitting: "Wird überprüft…",
      oneTimeNote:
        "Das Öffnen vernichtet es. Kopieren Sie, was Sie brauchen, bevor Sie den Tab schließen.",
      resend: "Code erneut senden",
      resendIn: "Erneut senden in {seconds} s",
      incorrect: "Falscher Code.",
      incorrectWithAttempts: plural({
        one: "Falscher Code (noch {count} Versuch).",
        other: "Falscher Code (noch {count} Versuche).",
      }),
    },

    success: {
      textTitle: "Das Geheimnis",
      fileTitle: "Ihre Datei",
      destroyedOnServer: "auf dem Server vernichtet",
      clickToReveal: "Zum Anzeigen klicken",
      sendOneBack: "Eines zurückschicken",
      fileDownloaded: "{filename} wurde in Ihrem Browser entschlüsselt und heruntergeladen.",
      destroyedNotice:
        "Dieses Geheimnis wurde auf dem Server vernichtet. Ein Neuladen dieser Seite zeigt nichts mehr. Bestätigt als {email} um {time}.",
    },
  },

  reply: {
    title: "Etwas zurückschicken",
    intro: "Das geht an {email}, und nur diese Person kann es öffnen. Sie können eine Antwort senden.",
    receiptTo: "Die Lesebestätigung geht an {email}, die Adresse, die Sie gerade bestätigt haben.",
    expired: {
      title: "Dieses Antwortfenster ist geschlossen",
      body: "Eine Antwort lässt sich nur von der Seite aus senden, auf der Sie ein Geheimnis geöffnet haben, und nur kurze Zeit danach. Bitten Sie den Absender um einen neuen Link.",
    },
  },

  auth: {
    signInRequired: "Zum Erstellen eines neuen Links ist eine Anmeldung erforderlich.",
    expired: {
      title: "Ihre Sitzung ist abgelaufen",
      body: "Sie wurden abgemeldet, weil diese Seite längere Zeit geöffnet war. Melden Sie sich erneut an, um einen Link zu erstellen. Bereits Eingegebenes wurde nicht gespeichert.",
    },
    signIn: "Anmelden",
    signedInAs: "Angemeldet als {email}",
    signOut: "Abmelden",
  },

  apiError: {
    rateLimited: "Zu viele Anfragen. Warten Sie einen Moment und versuchen Sie es erneut.",
    tooLarge: "Diese Datei ist zu groß zum Teilen.",
    unauthenticated: "Ihre Sitzung ist abgelaufen. Melden Sie sich erneut an, um einen Link zu erstellen.",
    webhookHostNotPermitted: "Diese Webhook-URL zeigt auf ein Ziel, an das dieser Server nicht sendet.",
    webhooksDisabled: "Webhooks sind auf diesem Server deaktiviert.",
    validationFailed: "Einige der Angaben oben wurden nicht akzeptiert. Prüfen Sie sie und versuchen Sie es erneut.",
  },
};
